import React, { useEffect, useRef, useState } from 'react';
import { Alert, Icon } from '@amzn/storm-ui';
import { BANNER_TYPE, LocalNotification } from '@amzn/aac-chrome-internal-api';
import { bannerIconMap, bannerMetricsPublisher } from '../types';
import { logWithTakt } from 'src/commons/utils/logWithTakt';
import { BANNER_LEVEL } from 'src/app/slice';
import { getPartitionedBanners } from '../bannerUtils';
import { BannersModal } from '../Common/BannersModal';
import { AcesBannerContent } from 'src/commons/components/ConsolidatedBanner/AcesBannerContent';
import { BannerLink } from 'src/commons/components/ConsolidatedBanner/styles';
import {
  useHeaderBundle,
  usePageHeaderBundle,
} from 'src/providers/PantherLocalizationProvider';
import { BannerText, TruncatedText, TruncatedTextWrapper } from '../styles';

interface SingleBannerWithModalProps {
  banner: LocalNotification;
  onDismissClick: (dismissBanner: LocalNotification) => void;
  bannerLevel: BANNER_LEVEL;
  isRenderedInModal?: boolean;
}

export const SingleBannerWithModal: React.VFC<SingleBannerWithModalProps> = ({
  banner,
  onDismissClick,
  bannerLevel,
  isRenderedInModal = true,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const learnMoreButtonRef = useRef(null);
  const pageHeaderTranslation = usePageHeaderBundle();
  const headerTranslation = useHeaderBundle();
  const learnMoreAriaLabel = headerTranslation.getMessage(
    'console-banner-learn-more-single-message-aria-label',
  );

  useEffect(() => {
    bannerMetricsPublisher[bannerLevel].publishCounterMonitor(
      `${bannerLevel}BannerRendered:${banner.id}`,
      1,
    );
    logWithTakt(`${bannerLevel}BannerRendered`, { bannerId: banner.id });
  }, []);

  const clickLinkHandler = (event) => {
    event?.preventDefault();
    bannerMetricsPublisher[bannerLevel].publishCounterMonitor(
      `bannerLinkClicked:${banner.id}`,
      1,
    );
    logWithTakt(`${bannerLevel}BannerClicked`, {
      bannerId: banner.id,
    });
    setIsModalOpen(true);
  };

  return (
    <>
      <Alert
        data-test-id="single-banner"
        type={banner.type.toLowerCase() as BANNER_TYPE}
        isSolid
        withCloseButton={banner.type !== BANNER_TYPE.ERROR}
        onClose={() => onDismissClick(banner)}
      >
        <TruncatedTextWrapper>
          {isRenderedInModal && <Icon type={bannerIconMap[banner.type]} />}
          <TruncatedText>
            {banner.acesBannerContent ? (
              <AcesBannerContent
                acesBannerContent={banner.acesBannerContent}
                bannerLevel={bannerLevel}
                bannerId={banner.id}
                bannerType={banner.type}
              />
            ) : (
              <BannerText>{banner.title || banner.message}</BannerText>
            )}
          </TruncatedText>
          <BannerLink
            href="#"
            ref={learnMoreButtonRef}
            onClick={clickLinkHandler}
            isRenderedInModal={isRenderedInModal}
            bannerType={banner.type}
            aria-label={learnMoreAriaLabel}
          >
            {pageHeaderTranslation.getMessage(
              'page-header-banner-learn-more-text',
            )}
          </BannerLink>
        </TruncatedTextWrapper>
      </Alert>
      <BannersModal
        partitionedBanners={getPartitionedBanners([banner])}
        isModalOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        bannerLevel={bannerLevel}
      />
    </>
  );
};
